import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { CheckCircle2, Circle, TrendingUp, Users, DollarSign, Calendar, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatINR } from '../lib/utils';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { AtmosphereUploader } from '../components/AtmosphereUploader';
import { AtmospherePortrait } from '../components/AtmospherePortrait';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiQuery, setAiQuery] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [createWeddingOpen, setCreateWeddingOpen] = useState(false);
  const [weddingForm, setWeddingForm] = useState({
    bride_name: '',
    groom_name: '',
    wedding_date: '',
    venue: ''
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/dashboard/stats`, {
        withCredentials: true,
      });
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWedding = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/weddings`, weddingForm, { withCredentials: true });
      toast.success('Wedding created successfully!');
      setCreateWeddingOpen(false);
      window.location.reload();
    } catch (error) {
      toast.error('Error creating wedding');
    }
  };

  const handleAIQuery = async () => {
    if (!aiQuery.trim()) return;
    setAiLoading(true);
    setAiResponse('');
    
    try {
      const response = await fetch(`${API_URL}/api/ai/summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ query: aiQuery })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const content = line.slice(6);
            if (content === '[DONE]') break;
            setAiResponse(prev => prev + content);
          }
        }
      }
    } catch (error) {
      toast.error('Error getting AI response');
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user?.wedding_id) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="create-wedding-prompt">
        <Card className="shadow-[0_8px_30px_rgba(139,30,65,0.04)]">
          <CardHeader className="text-center pb-8">
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-secondary/10 flex items-center justify-center">
                <Calendar className="h-8 w-8 text-secondary" />
              </div>
            </div>
            <CardTitle className="text-3xl font-serif">Create Your Wedding</CardTitle>
            <CardDescription>Let's start planning your dream Indian wedding</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateWedding} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bride's Name</Label>
                  <Input
                    placeholder="Bride's name"
                    value={weddingForm.bride_name}
                    onChange={(e) => setWeddingForm({...weddingForm, bride_name: e.target.value})}
                    required
                    data-testid="bride-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Groom's Name</Label>
                  <Input
                    placeholder="Groom's name"
                    value={weddingForm.groom_name}
                    onChange={(e) => setWeddingForm({...weddingForm, groom_name: e.target.value})}
                    required
                    data-testid="groom-name-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Wedding Date (Optional)</Label>
                <Input
                  type="date"
                  value={weddingForm.wedding_date}
                  onChange={(e) => setWeddingForm({...weddingForm, wedding_date: e.target.value})}
                  data-testid="wedding-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Venue (Optional)</Label>
                <Input
                  placeholder="Wedding venue"
                  value={weddingForm.venue}
                  onChange={(e) => setWeddingForm({...weddingForm, venue: e.target.value})}
                  data-testid="venue-input"
                />
              </div>
              <Button type="submit" className="w-full rounded-full" data-testid="create-wedding-button">
                Create Wedding
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-content">
      <div className="flex flex-col-reverse gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 space-y-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1 className="text-4xl font-serif font-bold tracking-tight mb-2 text-foreground">
                Welcome, {user?.name}
              </h1>
              <p className="text-muted-foreground">Here&apos;s your wedding planning overview</p>
            </div>
            <AtmosphereUploader />
          </div>
        </div>
        <AtmospherePortrait />
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tasks Progress</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="tasks-stat">
              {stats?.completed_tasks}/{stats?.total_tasks}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Tasks completed</p>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Guest RSVPs</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="guests-stat">
              {stats?.confirmed_guests}/{stats?.total_guests}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Confirmed guests</p>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Budget</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="budget-stat">
              {formatINR(stats?.spent_budget || 0, { compact: true })}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              of {formatINR(stats?.total_budget || 0, { compact: true })} budget
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-sm bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Readiness Score</CardTitle>
            <TrendingUp className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary" data-testid="readiness-score">
              {stats?.readiness_score}%
            </div>
            <Progress value={stats?.readiness_score} className="mt-2" />
          </CardContent>
        </Card>
      </div>

      {(stats?.overdue_payments > 0 || stats?.upcoming_payments > 0) && (
        <Card className={`shadow-sm ${stats?.overdue_payments > 0 ? 'border-destructive/30 bg-destructive/5' : 'border-secondary/30 bg-secondary/5'}`} data-testid="payments-alert-card">
          <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className={`h-10 w-10 rounded-full flex items-center justify-center ${stats?.overdue_payments > 0 ? 'bg-destructive/10' : 'bg-secondary/10'}`}>
                <DollarSign className={`h-5 w-5 ${stats?.overdue_payments > 0 ? 'text-destructive' : 'text-secondary-foreground'}`} />
              </div>
              <div>
                <p className="font-medium">
                  {stats?.overdue_payments > 0
                    ? `${stats.overdue_payments} overdue payment${stats.overdue_payments > 1 ? 's' : ''}`
                    : `${stats.upcoming_payments} upcoming payment${stats.upcoming_payments > 1 ? 's' : ''}`}
                </p>
                <p className="text-sm text-muted-foreground">
                  {formatINR(stats?.total_payment_due || 0, { compact: true })} outstanding to vendors
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/payments'} className="rounded-full" data-testid="view-payments-button">
              View Payments
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="shadow-[0_8px_30px_rgba(139,30,65,0.04)]">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-secondary" />
            <CardTitle className="text-xl font-serif">AI Wedding Assistant</CardTitle>
          </div>
          <CardDescription>Ask me anything about your wedding planning</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="e.g., What tasks should I prioritize this week?"
              value={aiQuery}
              onChange={(e) => setAiQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAIQuery()}
              data-testid="ai-query-input"
              disabled={aiLoading}
            />
            <Button
              onClick={handleAIQuery}
              disabled={aiLoading || !aiQuery.trim()}
              data-testid="ai-query-button"
              className="rounded-full"
            >
              {aiLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Ask'}
            </Button>
          </div>
          {aiResponse && (
            <div className="p-4 bg-muted/50 rounded-lg" data-testid="ai-response">
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{aiResponse}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};